// ─── The brain: what the assistant is taught ────────────────────────────────
//
// Three kinds of knowledge, and the differences matter:
//   instruction — text that goes into the prompt verbatim
//   template    — a saved reply sent word for word. The model is told only its
//                 NAME, never its text, which is what stops it retyping a RIB
//   image       — a picture a HUMAN can send, plus a description. The bytes
//                 never reach the model; the description is all it can reason
//                 about, which is why it is required
//
// Everything enabled here is injected on EVERY draft, so this page is also the
// cost lever. Said on screen rather than left to be discovered.
//
// ─── WHAT THIS REWRITE FIXED ────────────────────────────────────────────────
//
// ⚠️ THE SALES SECTION WAS INVISIBLE. `assistant_knowledge.section` has existed
// since the Ventes work and three sales instructions were live in it — but the
// page rendered one flat list, so they sat unlabelled among nineteen general
// ones, impossible to find or edit. The column was always returned by
// `select('*')`; nothing read it.
//
// ⚠️ AND THE CONTENT WAS BELOW THREE EMPTY FORMS. You scrolled past three
// "add" panels before seeing anything the assistant actually knows. Adding is
// the rare act; reading and correcting is the common one, so the lists come
// first and the forms live behind a button.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { whatsappApi, type AssistantKnowledge } from '@/lib/api';
import { BulkImageDrop } from './BulkImageDrop';
import { Card, EmptyState, SettingGroup } from './_ui';
import { useT } from '@/lib/i18n';

const inputCls =
  'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white transition-colors duration-200 ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent';

type Section = 'general' | 'sales';

/**
 * A Brain file's preview. A video is shown as a video (owner, 2026-10-01: « the
 * Brain panel shows a broken thumbnail for video ») — an <img> cannot draw an
 * .mp4. Matches the worker's lib/knowledgeUpload.ts isKnowledgeVideo.
 */
export function KnowledgeThumb({ url, storagePath, title }: { url: string; storagePath: string | null; title: string }) {
  const cls = 'w-14 h-14 rounded-lg object-cover border border-gray-100 shrink-0';
  if (/\.(mp4|mov|m4v)$/i.test(storagePath ?? '')) {
    return <video src={url} controls muted playsInline preload="metadata" aria-label={title} className={`${cls} bg-black`} />;
  }
  return <img src={url} alt={title} className={cls} />;
}

function Row({ entry }: { entry: AssistantKnowledge }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(entry.title);
  const [body, setBody] = useState(entry.body ?? '');
  const invalidate = () => qc.invalidateQueries({ queryKey: ['assistant-knowledge'] });

  const saveMut = useMutation({
    mutationFn: () => whatsappApi.updateKnowledge(entry.id, { title, body }),
    onSuccess: () => { setEditing(false); invalidate(); },
  });
  const patchMut = useMutation({
    mutationFn: (p: Record<string, unknown>) => whatsappApi.updateKnowledge(entry.id, p),
    onSuccess: invalidate,
  });
  const delMut = useMutation({ mutationFn: () => whatsappApi.deleteKnowledge(entry.id), onSuccess: invalidate });

  const missingDescription = entry.kind === 'image' && !(entry.body ?? '').trim();
  const isTemplate = entry.kind === 'template';

  return (
    <div className={`border rounded-xl p-3.5 bg-white transition-colors duration-200 ${
      entry.enabled ? 'border-gray-200' : 'border-gray-100 opacity-60'
    }`}>
      <div className="flex items-start gap-3">
        {entry.kind === 'image' && entry.url && (
          <KnowledgeThumb url={entry.url} storagePath={entry.storage_path} title={entry.title} />
        )}
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="space-y-2">
              <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
              <textarea
                className={`${inputCls} min-h-[80px] ${isTemplate ? 'font-mono text-[12px]' : ''}`}
                value={body} onChange={(e) => setBody(e.target.value)}
                placeholder={entry.kind === 'image' ? t('Décrivez la photo…') : t('Instruction…')}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => saveMut.mutate()} loading={saveMut.isPending}>{t('Enregistrer')}</Button>
                <button className="text-[11.5px] text-gray-500 underline cursor-pointer" onClick={() => setEditing(false)}>
                  {t('Annuler')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-[13px] font-semibold text-gray-800">{entry.title}</p>
              <p className={`text-[11.5px] text-gray-600 mt-0.5 whitespace-pre-wrap leading-snug ${
                isTemplate ? 'font-mono text-[11px]' : ''
              }`}>
                {entry.body || <span className="text-gray-400">{t('(vide)')}</span>}
              </p>
              {/* An image with no description is invisible to the assistant —
                  worth saying loudly, because the row otherwise looks fine. */}
              {missingDescription && (
                <p className="text-[11px] text-amber-700 mt-1">
                  {t('Sans description, l’assistant ne sait pas ce qu’il y a sur cette image.')}
                </p>
              )}
            </>
          )}
        </div>
        {!editing && (
          <div className="flex items-center gap-2 shrink-0">
            <label className="flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer">
              <input type="checkbox" checked={entry.enabled} className="accent-brand-600"
                onChange={() => patchMut.mutate({ enabled: !entry.enabled })} />
              {t('actif')}
            </label>
            <button className="text-[11.5px] text-gray-500 underline cursor-pointer" onClick={() => setEditing(true)}>
              {t('Modifier')}
            </button>
            <button
              className="text-[11.5px] text-red-600 underline cursor-pointer"
              onClick={() => { if (window.confirm(t('Supprimer ?'))) delMut.mutate(); }}
            >{t('Supprimer')}</button>
          </div>
        )}
      </div>
      {/* Moving an entry between sections is an edit, not a re-creation. */}
      {!editing && entry.kind === 'instruction' && (
        <button
          className="text-[11px] text-gray-400 hover:text-gray-700 underline mt-2 cursor-pointer"
          onClick={() => patchMut.mutate({ section: entry.section === 'sales' ? 'general' : 'sales' })}
        >
          {entry.section === 'sales' ? t('→ Déplacer vers Général') : t('→ Déplacer vers Ventes')}
        </button>
      )}
    </div>
  );
}

/**
 * What a general rule is ABOUT, worked out from its own words.
 *
 * ─── WHY THIS IS DERIVED AND NOT STORED ─────────────────────────────────────
 *
 * Nineteen general rules rendered as nineteen identical cards. The list was
 * alphabetical-by-nothing, so finding "what do we tell people about money" meant
 * reading all nineteen every time.
 *
 * ⚠️ `assistant_knowledge` HAS NO SUBJECT COLUMN. `section` exists and means
 * something else entirely (general vs sales, and it drives the « Déplacer vers
 * Ventes » action), so it cannot carry this. Adding a column would be a schema
 * change and a migration for a layout pass — this reads the title and body
 * instead.
 *
 * ⚠️ THAT MAKES IT A GUESS, AND IT IS BUILT TO GUESS SAFELY. Order is
 * money-first, matching the escalation router's reasoning: a rule about price
 * that lands under « Autres » is a nuisance, a price rule filed under « Ton »
 * is one nobody looks at before quoting. Anything unmatched goes to « Autres »,
 * which is always rendered — a rule can be in the wrong group but never in no
 * group, and the count in every header makes a wrong split visible.
 *
 * ⚠️ NO BARE `\d`. Latin-letter Darija writes 3/7/9/2 as letters, and several
 * of these rules are written in it.
 */
const SUBJECTS: Array<{ key: string; label: string; hint: string; re: RegExp }> = [
  {
    key: 'money', label: 'Prix et paiement',
    hint: 'ce qu’il dit sur l’argent',
    re: /prix|tarif|pack|paiement|payer|avance|acompte|banque|rib|virement|facture|remise|relief|argent|khalas|taman|flous/i,
  },
  {
    key: 'photos', label: 'Photos et pages',
    hint: 'comment les envoyer, combien il en faut',
    re: /photo|tsawer|image|page|document|qualit[ée]|r[ée]solution|montage/i,
  },
  {
    key: 'delivery', label: 'Livraison et étapes',
    hint: 'délais, suivi, où en est la commande',
    re: /livraison|livrer|d[ée]lai|colis|[ée]tape|exp[ée]dition|suivi|commande.*[ée]tape|tawsil/i,
  },
  {
    key: 'tone', label: 'Ton et façon de parler',
    hint: 'langue, longueur, une question à la fois',
    re: /ton\b|langue|darija|fran[çc]ais|question|r[ée]ponse|message|silence|vouvoie|tutoie|emoji/i,
  },
  {
    key: 'never', label: 'Ce qu’il ne fait jamais',
    hint: 'les interdits',
    re: /interdit|jamais|ne pas|refus|influenceur|vip|promet|invent/i,
  },
];

/** Bucket the general rules. Every rule lands somewhere; `other` is always
 *  rendered even when the label is vague, because a hidden rule is worse than
 *  an untidy one. */
function bySubject(rows: AssistantKnowledge[]) {
  const out = new Map<string, AssistantKnowledge[]>();
  const other: AssistantKnowledge[] = [];
  for (const r of rows) {
    const hay = `${r.title} ${r.body ?? ''}`;
    const hit = SUBJECTS.find((s) => s.re.test(hay));
    if (!hit) { other.push(r); continue; }
    out.set(hit.key, [...(out.get(hit.key) ?? []), r]);
  }
  return { out, other };
}


export function BrainTab() {
  const { t } = useT();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['assistant-knowledge'] });

  const { data, isLoading } = useQuery({ queryKey: ['assistant-knowledge'], queryFn: () => whatsappApi.knowledge() });
  const rows = data?.knowledge ?? [];
  const enabled = data?.assistant_enabled ?? false;

  const [adding, setAdding] = useState<null | 'instruction' | 'template' | 'image'>(null);
  const [q, setQ] = useState('');
  const [insTitle, setInsTitle] = useState('');
  const [insBody, setInsBody] = useState('');
  const [insSection, setInsSection] = useState<Section>('general');
  const [tplTitle, setTplTitle] = useState('');
  const [tplBody, setTplBody] = useState('');

  const toggleMut = useMutation({ mutationFn: (v: boolean) => whatsappApi.setEnabled(v), onSuccess: invalidate });
  const addInsMut = useMutation({
    mutationFn: () => whatsappApi.addKnowledge({
      kind: 'instruction', title: insTitle.trim(), body: insBody.trim(), section: insSection,
    }),
    onSuccess: () => { setInsTitle(''); setInsBody(''); setAdding(null); invalidate(); },
  });
  const addTplMut = useMutation({
    mutationFn: () => whatsappApi.addKnowledge({ kind: 'template', title: tplTitle.trim(), body: tplBody }),
    onSuccess: () => { setTplTitle(''); setTplBody(''); setAdding(null); invalidate(); },
  });

  // 31 entries is past the point where a flat list can be scanned.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      r.title.toLowerCase().includes(needle) || (r.body ?? '').toLowerCase().includes(needle));
  }, [rows, q]);

  const general = filtered.filter((r) => r.kind === 'instruction' && r.section !== 'sales');
  const sales = filtered.filter((r) => r.kind === 'instruction' && r.section === 'sales');
  const templates = filtered.filter((r) => r.kind === 'template');
  const images = filtered.filter((r) => r.kind === 'image');
  const activeCount = rows.filter((r) => r.enabled).length;
  const subjects = bySubject(general);

  return (
    <div className="space-y-4">
      {/* Master switch. Off by default and stated plainly, because the paired
          line is the real support number. */}
      <div className={`rounded-xl border p-4 transition-colors duration-300 ${
        enabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'
      }`}>
        <label className="flex items-start gap-3 cursor-pointer">
          {/* ⚠️ 2026-09-26: this fired twice (ON then OFF, 140 ms apart) and the
              assistant answered nobody for 19 hours. Locked while saving, and
              switching it OFF for every customer is asked, never assumed. */}
          <input
            type="checkbox" checked={enabled} className="mt-0.5 accent-brand-600 w-4 h-4 disabled:opacity-50"
            disabled={toggleMut.isPending}
            onChange={(e) => {
              const next = e.target.checked;
              if (!next && !window.confirm(t('Éteindre l’assistant ?\n\nIl ne répondra plus à AUCUN client (ni préparation, ni envoi) jusqu’à ce que vous le rallumiez ici.'))) return;
              toggleMut.mutate(next);
            }}
          />
          <span>
            <span className="block text-[13px] font-semibold text-gray-800">
              {t('Laisser l’assistant préparer des réponses')}
            </span>
            <span className="block text-[11.5px] text-gray-600 mt-0.5 leading-snug">
              {/* ⚠️ THIS LINE USED TO SAY THE ASSISTANT NEVER SENDS ANYTHING ON ITS OWN.
                  It sends, on its own, to every number on the test list — 17 of them
                  the day this was corrected. Copy written before sending existed
                  had outlived the code by three weeks. What it says now is true in
                  every switch state, because it names the switch. */}
              {t('Il rédige les réponses. Elles partent toutes seules uniquement si « Envoyer automatiquement » est activé (onglet Numéros), et uniquement vers les numéros de cette liste. Pour tous les autres clients, un humain relit et envoie.')}
            </span>
          </span>
        </label>
      </div>

      {/* ── Search + add. Adding is the rare act, so it hides behind a button. ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          className={`${inputCls} max-w-xs`} value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={t('Rechercher dans le cerveau…')}
        />
        <span className="text-[11.5px] text-gray-500">
          {t('{a} actives sur {n} — toutes envoyées à chaque message', {
            a: String(activeCount), n: String(rows.length),
          })}
        </span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant={adding === 'instruction' ? 'primary' : 'secondary'}
            onClick={() => setAdding(adding === 'instruction' ? null : 'instruction')}>
            {t('+ Instruction')}
          </Button>
          <Button size="sm" variant={adding === 'template' ? 'primary' : 'secondary'}
            onClick={() => setAdding(adding === 'template' ? null : 'template')}>
            {t('+ Réponse enregistrée')}
          </Button>
          <Button size="sm" variant={adding === 'image' ? 'primary' : 'secondary'}
            onClick={() => setAdding(adding === 'image' ? null : 'image')}>
            {t('+ Image')}
          </Button>
        </div>
      </div>

      {adding === 'instruction' && (
        <Card className="storiz-pop-in">
          <p className="text-[11.5px] text-gray-500 mb-2.5 leading-snug">
            {t('Prix, délais, règles de livraison, ce qu’il ne doit jamais dire. Tout ce qui est actif est envoyé à chaque message — restez court.')}
          </p>
          <div className="space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_150px] gap-2">
              <input className={inputCls} value={insTitle} onChange={(e) => setInsTitle(e.target.value)}
                placeholder={t('Titre, ex. Livraison')} />
              <select className={inputCls} value={insSection}
                onChange={(e) => setInsSection(e.target.value as Section)}>
                <option value="general">{t('Général')}</option>
                <option value="sales">{t('Ventes')}</option>
              </select>
            </div>
            <textarea className={`${inputCls} min-h-[80px]`} value={insBody}
              onChange={(e) => setInsBody(e.target.value)}
              placeholder={t('Ex. La livraison est gratuite partout au Maroc, 3 à 5 jours.')} />
            <Button size="sm" onClick={() => addInsMut.mutate()} loading={addInsMut.isPending}
              disabled={!insTitle.trim() || !insBody.trim()}>{t('Ajouter')}</Button>
            {addInsMut.isError && (
              <p className="text-[11.5px] text-red-600" role="alert">{(addInsMut.error as Error).message}</p>
            )}
          </div>
        </Card>
      )}

      {adding === 'template' && (
        <Card className="storiz-pop-in">
          <p className="text-[11.5px] text-gray-500 mb-2.5 leading-snug">
            {t('Un message envoyé mot pour mot, sans être réécrit. C’est ici que va le message avec le RIB : l’assistant ne connaît pas le texte, il ne peut que le demander par son nom. Aucun chiffre ne peut être inventé.')}
          </p>
          <div className="space-y-2">
            <input className={inputCls} value={tplTitle} onChange={(e) => setTplTitle(e.target.value)}
              placeholder={t('Nom, ex. RIB / Avance')} />
            <textarea className={`${inputCls} min-h-[110px] font-mono text-[12px]`} value={tplBody}
              onChange={(e) => setTplBody(e.target.value)}
              placeholder={t('Collez ici le message exact, avec le RIB, tel que vous l’envoyez d’habitude.')} />
            <Button size="sm" onClick={() => addTplMut.mutate()} loading={addTplMut.isPending}
              disabled={!tplTitle.trim() || !tplBody.trim()}>{t('Enregistrer la réponse')}</Button>
            {addTplMut.isError && (
              <p className="text-[11.5px] text-red-600" role="alert">{(addTplMut.error as Error).message}</p>
            )}
          </div>
        </Card>
      )}

      {adding === 'image' && (
        <Card className="storiz-pop-in">
          {/* ⚠️ ONE DESCRIPTION PER IMAGE, NOT ONE PER BATCH. The assistant never
              sees the picture — the description is the only thing that makes it
              findable, so twelve images sharing one box would be twelve entries
              it cannot tell apart. See BulkImageDrop / imageQueue.ts. */}
          <BulkImageDrop onUploaded={invalidate} />
        </Card>
      )}

      {isLoading && <p className="text-[12.5px] text-gray-400">{t('Chargement…')}</p>}

      {/* ── What it knows, grouped. Sales was invisible before this. ── */}
      {/* ── GROUPED BY SUBJECT, CLOSED ────────────────────────────────────
          Nineteen general rules rendered as nineteen identical cards, so
          answering "what do we tell people about money" meant reading all of
          them. Now each subject is one line with a count.

          ⚠️ `key={q}` AND `defaultOpen={Boolean(q)}` TOGETHER ARE THE SEARCH
          FIX. Collapsing the list would otherwise break the search box above:
          a match sitting inside a closed group is a match the user cannot see,
          and they would conclude the rule does not exist. Remounting on every
          query change is what makes the groups spring open with results in
          them, and fall closed again when the box is cleared. ── */}
      {general.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-baseline gap-2">
            <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              {t('Règles générales')}
            </h3>
            <span className="text-[11px] text-gray-400 tabular-nums">{general.length}</span>
          </div>
          {SUBJECTS.map((subject) => {
            const inSubject = subjects.out.get(subject.key) ?? [];
            if (!inSubject.length) return null;
            const off = inSubject.filter((r) => !r.enabled).length;
            return (
              <SettingGroup
                key={`${subject.key}:${q}`}
                title={t(subject.label)}
                defaultOpen={Boolean(q)}
                summary={`${inSubject.length} ${t('règle(s)')} · ${t(subject.hint)}`
                  + (off ? ` · ${off} ${t('coupée(s)')}` : '')}
              >
                <div className="space-y-2">
                  {inSubject.map((r) => <Row key={r.id} entry={r} />)}
                </div>
              </SettingGroup>
            );
          })}
          {/* Always rendered when it has anything. A rule can be in the wrong
              group; it must never be in no group. */}
          {subjects.other.length > 0 && (
            <SettingGroup
              key={`other:${q}`}
              title={t('Autres')}
              defaultOpen={Boolean(q)}
              summary={`${subjects.other.length} ${t('règle(s)')} · ${t('sujet non reconnu')}`}
            >
              <div className="space-y-2">
                {subjects.other.map((r) => <Row key={r.id} entry={r} />)}
              </div>
            </SettingGroup>
          )}
        </div>
      )}

      {sales.length > 0 && (
        <SettingGroup
          key={`sales:${q}`}
          title={t('Ventes')}
          defaultOpen={Boolean(q)}
          summary={`${sales.length} ${t('règle(s)')} · ${t('comment proposer une option en plus')}`}
        >
          <div className="space-y-2">{sales.map((r) => <Row key={r.id} entry={r} />)}</div>
        </SettingGroup>
      )}

      {templates.length > 0 && (
        <SettingGroup
          key={`tpl:${q}`}
          title={t('Réponses enregistrées')}
          defaultOpen={Boolean(q)}
          summary={`${templates.length} ${t('réponse(s)')} · ${t('envoyées mot pour mot')}`}
        >
          <div className="space-y-2">{templates.map((r) => <Row key={r.id} entry={r} />)}</div>
        </SettingGroup>
      )}

      {images.length > 0 && (
        <SettingGroup
          key={`img:${q}`}
          title={t('Images')}
          defaultOpen={Boolean(q)}
          summary={`${images.length} ${t('image(s)')}`}
        >
          <div className="space-y-2">{images.map((r) => <Row key={r.id} entry={r} />)}</div>
        </SettingGroup>
      )}

      {!isLoading && !rows.length && (
        <Card className="border-dashed">
          <div className="text-center py-6">
            <p className="text-[13px] font-medium text-gray-700">{t('L’assistant ne sait encore rien.')}</p>
            <p className="text-[11.5px] text-gray-500 mt-1">
              {t('Ajoutez vos prix, vos délais et vos règles pour qu’il puisse répondre correctement.')}
            </p>
          </div>
        </Card>
      )}
      {!isLoading && rows.length > 0 && !filtered.length && (
        <EmptyState>{t('Rien ne correspond à « {q} ».', { q })}</EmptyState>
      )}
    </div>
  );
}
